/**
 * Homepage Connectors Marquee
 *
 * Dual-row ecosystem sample from the redesign prototype.
 * Uses real brand icons instead of colored dots.
 */

'use client'

import type { CSSProperties } from 'react'
import type { IconType } from 'react-icons'
import {
  SiAirtable,
  SiAsana,
  SiBrave,
  SiCloudflare,
  SiConfluence,
  SiDiscord,
  SiDocker,
  SiFigma,
  SiGithub,
  SiGoogledrive,
  SiHubspot,
  SiIntercom,
  SiJira,
  SiLinear,
  SiMailchimp,
  SiPostgresql,
  SiRedis,
  SiSentry,
  SiShopify,
  SiSqlite,
  SiStripe,
  SiTelegram,
  SiZendesk
} from 'react-icons/si'
import { FaSalesforce, FaSlack } from 'react-icons/fa6'
import { TbBrandTwilio } from 'react-icons/tb'

import { cn } from '@/lib/utils'

export interface ConnectorTile {
  name: string
  color: string
  emphasize?: boolean
}

interface HomepageConnectorsProps {
  eyebrow: string
  heading: string
  subhead: string
  rowOne: ConnectorTile[]
  rowTwo: ConnectorTile[]
}

function BrandPathIcon({
  path,
  title,
  className,
  style
}: {
  path: string
  title: string
  className?: string
  style?: CSSProperties
}) {
  return (
    <svg
      role="img"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
      aria-hidden
    >
      <title>{title}</title>
      <path d={path} fill="currentColor" />
    </svg>
  )
}

const PlaywrightIcon: IconType = ({ className, style }) => (
  <BrandPathIcon
    title="Playwright"
    className={className}
    style={style}
    path="M23.996 7.462c-.056.837-.257 2.135-.716 3.85-.995 3.715-4.27 10.874-10.42 9.227-6.15-1.65-5.407-9.487-4.412-13.201.46-1.716.934-2.94 1.305-3.694.42-.853.846-.289 1.815.523.684.573 2.41 1.791 5.011 2.488 2.601.697 4.706.506 5.583.352 1.245-.219 1.897-.494 1.834.455Zm-9.807 3.863s-.127-1.819-1.773-2.286c-1.644-.467-2.613 1.04-2.613 1.04Zm4.058 4.539-7.769-2.172s.446 2.306 3.338 3.153c2.862.836 4.43-.98 4.43-.981Zm2.701-2.51s-.13-1.818-1.773-2.286c-1.644-.469-2.612 1.038-2.612 1.038ZM8.57 18.23c-4.749 1.279-7.261-4.224-8.021-7.08C.197 9.831.044 8.832.003 8.188c-.047-.73.455-.52 1.415-.354.677.118 2.3.261 4.308-.28a11.28 11.28 0 0 0 2.41-.956c-.058.197-.114.4-.17.61-.433 1.618-.827 4.055-.632 6.426-1.976.732-2.267 2.423-2.267 2.423l2.524-.715c.227 1.002.6 1.987 1.15 2.838a5.914 5.914 0 0 1-.171.049Zm-4.188-6.298c1.265-.333 1.363-1.631 1.363-1.631l-3.374.888s.745 1.076 2.01.743Z"
  />
)

const CONNECTOR_ICONS: Record<
  string,
  { Icon: IconType; color: string }
> = {
  GitHub: { Icon: SiGithub, color: '#181717' },
  Linear: { Icon: SiLinear, color: '#5E6AD2' },
  Slack: { Icon: FaSlack, color: '#4A154B' },
  Postgres: { Icon: SiPostgresql, color: '#4169E1' },
  Figma: { Icon: SiFigma, color: '#F24E1E' },
  Stripe: { Icon: SiStripe, color: '#635BFF' },
  Sentry: { Icon: SiSentry, color: '#362D59' },
  'Brave Search': { Icon: SiBrave, color: '#FB542B' },
  Playwright: { Icon: PlaywrightIcon, color: '#2EAD33' },
  Shopify: { Icon: SiShopify, color: '#7AB55C' },
  HubSpot: { Icon: SiHubspot, color: '#FF7A59' },
  Airtable: { Icon: SiAirtable, color: '#18BFFF' },
  Jira: { Icon: SiJira, color: '#0052CC' },
  Discord: { Icon: SiDiscord, color: '#5865F2' },
  'Google Drive': { Icon: SiGoogledrive, color: '#4285F4' },
  Salesforce: { Icon: FaSalesforce, color: '#00A1E0' },
  Zendesk: { Icon: SiZendesk, color: '#03363D' },
  Intercom: { Icon: SiIntercom, color: '#1F8DED' },
  Asana: { Icon: SiAsana, color: '#F06A6A' },
  Confluence: { Icon: SiConfluence, color: '#172B4D' },
  Telegram: { Icon: SiTelegram, color: '#26A5E4' },
  SQLite: { Icon: SiSqlite, color: '#003B57' },
  Redis: { Icon: SiRedis, color: '#FF4438' },
  Docker: { Icon: SiDocker, color: '#2496ED' },
  Twilio: { Icon: TbBrandTwilio, color: '#F22F46' },
  Cloudflare: { Icon: SiCloudflare, color: '#F38020' },
  Mailchimp: { Icon: SiMailchimp, color: '#FFE01B' }
}

function ConnectorMarqueeRow({
  tiles,
  reverse = false,
  label
}: {
  tiles: ConnectorTile[]
  reverse?: boolean
  label: string
}) {
  return (
    <div
      aria-label={label}
      className={cn('losi-connector-mq overflow-hidden', reverse && 'losi-connector-mq-rev')}
    >
      <div className="losi-connector-mq-track flex w-max">
        {[0, 1].map(setIndex => (
          <div
            key={setIndex}
            className="flex gap-3 pr-3"
            aria-hidden={setIndex === 1 ? true : undefined}
          >
            {tiles.map(tile => {
              const brand = CONNECTOR_ICONS[tile.name]
              const Icon = brand?.Icon
              const iconColor = brand?.color

              return (
                <span
                  key={`${setIndex}-${tile.name}`}
                  className={cn(
                    'inline-flex items-center gap-2.5 whitespace-nowrap rounded-xl border px-5 py-3 text-[15px] font-semibold transition-colors',
                    tile.emphasize
                      ? 'border-[rgb(39,67,240)] bg-[rgba(39,67,240,0.07)] text-[rgb(39,67,240)] dark:border-sky-400/50 dark:bg-sky-500/10 dark:text-sky-200'
                      : 'border-slate-200/90 bg-white text-slate-700 hover:border-[rgb(39,67,240)] dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:border-sky-400/40'
                  )}
                >
                  {!tile.emphasize && Icon ? (
                    <Icon
                      className="size-4 shrink-0"
                      style={{ color: iconColor }}
                      aria-hidden
                    />
                  ) : null}
                  {tile.name}
                </span>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}

export function HomepageConnectors({
  eyebrow,
  heading,
  subhead,
  rowOne,
  rowTwo
}: HomepageConnectorsProps) {
  return (
    <section className="relative mt-10 overflow-hidden py-10 lg:mt-12 lg:py-14">
      <div className="flex max-w-[64ch] flex-col gap-3 text-left">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
          {eyebrow}
        </p>
        <h2 className="font-display text-4xl font-medium leading-none tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
          {heading}
        </h2>
        <p className="text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
          {subhead}
        </p>
      </div>

      <div className="mt-8 flex flex-col gap-3.5">
        <ConnectorMarqueeRow tiles={rowOne} label={`${eyebrow} row one`} />
        <ConnectorMarqueeRow tiles={rowTwo} reverse label={`${eyebrow} row two`} />
      </div>
    </section>
  )
}
